import React from 'react';
import type { SelectProps } from 'antd';
import { Select } from 'antd';
import styled from 'styled-components';
import type { DefaultOptionType } from 'antd/es/select';
import classNames from 'classnames';
import Icon from '@/components/Icon/Icon';
import { getPrefixedDataAttributes } from '@/components/utils/DomUtils';
import type { DataAttributes } from '@/components/utils/TypeUtils';
import './SelectComponent.scss';

type RawValue = string | number;
interface LabeledValue {
  key?: string;
  value: RawValue;
  label: React.ReactNode;
}

type CustomSelectProps = SelectProps & {
  /** The name of the select dropdown. */
  name: string;
  /** Select options. Will get better perf than jsx definition. */
  options: DefaultOptionType[];
  /** The custom prefix icon. */
  prefixIcon?: React.ReactNode;
  /** Initial selected option. */
  defaultValue?: string | string[] | number | number[] | LabeledValue | LabeledValue[];
  /** Current selected option (considered as a immutable array). */
  value?: string | string[] | number | number[] | LabeledValue | LabeledValue[];
  /** Indicate loading state. */
  loading?: boolean;
  /** Specify content to show when no result matches. */
  notFoundContent?: React.ReactNode;
  /** The className of dropdown menu. */
  popupClassName?: string;
  /** Customize dropdown content. */
  dropdownRender?: (menu: React.ReactNode) => React.ReactNode;
  /** The style of dropdown menu. */
  dropdownStyle?: React.CSSProperties;
  /** Whether disabled select. */
  disabled?: boolean;
  /** Select Wrapper class name */
  selectWrapperClass?: string;
  /** Enable suffix icon. */
  suffixIcon?: boolean;
  /** The custom suffix icon. */
  customSuffixIcon?: React.ReactNode;
  /** The data-* attribute is used to store custom data private to the page or application.   */
  dataAttributes?: DataAttributes;
  /** The placeholder of the select. */
  placeholder?: string;
  /** Enable floating labels. */
  floatingLabel?: boolean;
  /** Select wrapper css properties. */
  selectWrapperStyles?: React.CSSProperties;
  /**
   * Size of the select.
   *
   * @default 'large'
   */
  variant?: 'large';
  /** Enable search functionality. */
  showSearch?: boolean;
  /** Called when select an option or input value change. */
  onChange?: (value: string, option: DefaultOptionType | DefaultOptionType[]) => void;
  /** Called when an option is selected, the params are option's value (or key) and option instance. */
  onSelect?: (value: string, option: DefaultOptionType | DefaultOptionType[]) => void;
  /** Callback function that is fired when input changed. */
  onSearch?: (value: string) => void;
  /** Called when dropdown scrolls. */
  onScroll?: (page: number) => void;
  /** Called when dropdown opens. */
  onDropdownOpen?: (open: boolean) => void;
};

const SelectWrapper = styled.div`
  position: relative;
`;

/**
 * Select is a type of input that allows users to choose one or more options from a list of choices.
 * The options are hidden by default and revealed when a user interacts with an element. It shows the currently selected option in its default collapsed state.
 */
const SelectComponent: React.FC<CustomSelectProps> = React.forwardRef<
  HTMLDivElement,
  CustomSelectProps
>((props: CustomSelectProps, ref) => {
  const {
    name,
    prefixIcon,
    children,
    defaultValue,
    value,
    disabled,
    loading,
    selectWrapperClass,
    suffixIcon,
    customSuffixIcon,
    dataAttributes,
    placeholder,
    floatingLabel,
    selectWrapperStyles,
    variant = 'large',
    options,
    showSearch = true,
    onChange,
    onSelect,
    onSearch,
    onScroll,
    onDropdownOpen,
    ...rest
  } = props;
  const isControlled = value !== undefined;
  const [selectValue, setSelectValue] = React.useState(defaultValue ?? null);
  const [page, setPage] = React.useState(1);
  const displayValue = isControlled ? value : selectValue;
  const prefixedDataAttributes = React.useMemo(
    () => getPrefixedDataAttributes(dataAttributes, 'Select'),
    [dataAttributes]
  );
  const selectStyle = React.useMemo(() => ({ width: '100%' }), []);
  const suffixIconNode = React.useMemo(() => {
    if (!suffixIcon) {
      return false;
    }

    if (!customSuffixIcon) {
      return <Icon source="/src/assets/svg/search.svg" />;
    }

    return customSuffixIcon;
  }, [suffixIcon, customSuffixIcon]);

  /**
   * Called when select an option or input value change.
   */
  const handleChange = React.useCallback(
    (selectedValue: string, option: DefaultOptionType | DefaultOptionType[]) => {
      if (isControlled && selectedValue === value) {
        return;
      }

      if (!isControlled) {
        setSelectValue(selectedValue);
      }
      onChange?.(selectedValue, option);
    },
    [isControlled, onChange, value]
  );

  /**
   * Called when an option is selected, the params are option's value (or key) and option instance.
   */
  const handleSelect = React.useCallback(
    (selectedValue: string | number | LabeledValue, option: DefaultOptionType) => {
      if (!isControlled) {
        setSelectValue(selectedValue);
      }
      onSelect?.(selectedValue, option);
    },
    [isControlled, onSelect]
  );

  /**
   * Callback function that is fired when input changed.
   */
  const handleSearch = React.useCallback(
    (searchValue: string) => {
      onSearch?.(searchValue);
    },
    [onSearch]
  );

  /**
   * Called when dropdown scrolls.
   */
  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      /** Use currentTarget instead of target. */
      const targetElement = e.currentTarget;

      /** If options are still loading, we won't be emit the onScroll event. */
      if (loading) {
        return;
      }

      if (targetElement.scrollTop + targetElement.offsetHeight === targetElement.scrollHeight) {
        setPage((prev) => prev + 1);
        onScroll?.(page);
      }
    },
    [onScroll, page, loading]
  );

  /**
   * Called when dropdown opens.
   */
  const handleDropdownVisibleChange = React.useCallback(
    (visible: boolean) => {
      onDropdownOpen?.(visible);
    },
    [onDropdownOpen]
  );

  /**
   * Select Wrapper class names
   */
  const selectWrapperClassNames = classNames('select-wrapper', selectWrapperClass, {
    'select-wrapper--option-selected': displayValue,
    'select-wrapper--prefix-icon': prefixIcon,
    'select-wrapper--suffix-icon': suffixIcon,
    'select-wrapper--floating-label': floatingLabel,
    [`select-wrapper--${variant}`]: variant,
    'select-wrapper--disabled': disabled,
  });
  /**
   * Select class names
   */
  const selectClassName = classNames('select-dropdown');
  const labelClassName = classNames('select-label', {
    'floating-label': floatingLabel,
    filled: displayValue,
  });
  const selectId = `${name}-select`;

  return (
    <SelectWrapper className={selectWrapperClassNames} style={selectWrapperStyles} ref={ref}>
      {prefixIcon && <div className="prefix-icon-wrapper">{prefixIcon}</div>}
      <Select
        id={selectId}
        className={selectClassName}
        suffixIcon={suffixIconNode}
        style={selectStyle}
        disabled={disabled}
        loading={loading}
        value={displayValue}
        options={options}
        showSearch={showSearch}
        onChange={handleChange}
        {...(onSelect ? { onSelect: handleSelect } : {})}
        {...(showSearch && onSearch ? { onSearch: handleSearch } : {})}
        {...(onScroll ? { onPopupScroll: handleScroll } : {})}
        {...(onDropdownOpen ? { onDropdownVisibleChange: handleDropdownVisibleChange } : {})}
        placeholder={floatingLabel || placeholder}
        {...prefixedDataAttributes}
        {...rest}
      />
      {floatingLabel && (
        <label htmlFor={selectId} className={labelClassName}>
          {placeholder}
        </label>
      )}
    </SelectWrapper>
  );
});

SelectComponent.displayName = 'Select';

export default SelectComponent;
